import { Entity } from "@rauricoste/core";

export type PileProps<T extends Entity> = {
  pile: T[];
};

export type Pushable<T> = {
  push: (item: T) => void;
};

export class Pile<T extends Entity> implements PileProps<T> {
  pile: T[];

  constructor(props: PileProps<T>) {
    Object.assign(this, props);
  }

  canTake(count: number) {
    return this.pile.length >= count;
  }
  moveCount(to: Pushable<T>, count: number) {
    const { pile: from } = this;
    if (!this.canTake(count)) {
      throw new Error(
        `cannot draw ${count} from a pile of size ${from.length}`
      );
    }
    const cards = from.splice(from.length - count, count);
    cards.forEach((card) => to.push(card));
    return { drawn: cards };
  }
  move<X extends T>(to: Pushable<X>, item: X) {
    const { pile: from } = this;
    const index = from.findIndex((_) => _.id === item.id);
    if (index === -1) {
      throw new Error(`cannot find ${item} in order to move it`);
    }
    from.splice(index, 1);
    to.push(item);
  }
  remove(item: T) {
    return this.move<T>([] as T[], item);
  }
  removeById(id: string) {
    this.remove(this.getById(id));
  }
  removeCount(count: number) {
    return this.moveCount([] as T[], count);
  }
  moveAll(to: Pushable<T>) {
    this.moveCount(to, this.pile.length);
  }
  isEmpty() {
    return !this.pile.length;
  }
  push<X extends T>(item: X) {
    this.pile.push(item);
  }
  pushAll<X extends T>(items: X[]) {
    items.forEach((item) => this.push(item));
  }
  last(): T | undefined {
    return this.pile.at(this.pile.length - 1);
  }
  replace(item: T, newItem: T) {
    const { pile } = this;
    const index = pile.indexOf(item);
    this.remove(item);
    this.pile = [...pile.slice(0, index), newItem, ...pile.slice(index)];
  }
  getById(id: string): T {
    const found = this.findById(id);
    if (!found) {
      throw new Error(`could not find item with id=${id}`);
    }
    return found;
  }
  findById(id: string): T | undefined {
    return this.pile.find((item) => item.id === id);
  }
  get length(): number {
    return this.pile.length;
  }
  static empty<T extends Entity>(): Pile<T> {
    return new Pile<T>({
      pile: [],
    });
  }
  static fromArray<T extends Entity>(array: T[]): Pile<T> {
    return new Pile<T>({
      pile: array,
    });
  }
}
